import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { RootState, AppDispatch } from 'store';
import PhoenixLoader from 'components/common/PhoenixLoader';
import FeatherIcon from 'feather-icons-react';
import { Badge, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLaptop,
  faTag,
  faExclamationCircle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchMyRequests,
  cancelRequestThunk,
  selectMyRequests,
  selectAllRequestsLoading,
  selectAllRequestsError
} from 'store/allRequestsSlice';
import { AssetRequest } from 'api/assetsAPI';
import DeleteAllRequestModal from 'components/modals/DeleteAllRequestModal';

interface MyRequestsTableProps {
  searchQuery?: string;
  selectedCompanyId?: number | null;
}

const MyRequestsTable = ({
  searchQuery = '',
  selectedCompanyId = null
}: MyRequestsTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AssetRequest | null>(
    null
  );

  // Select data from Redux store
  const requests = useSelector(selectMyRequests);
  const loading = useSelector(selectAllRequestsLoading);
  const error = useSelector(selectAllRequestsError);

  useEffect(() => {
    dispatch(fetchMyRequests());
  }, [dispatch]);

  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      // Filter by company_id if selectedCompanyId is not null
      if (
        selectedCompanyId !== null &&
        request.company_id !== selectedCompanyId
      ) {
        return false;
      }

      // Filter by search query if provided
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase().trim();
      return (
        request.asset_name?.toLowerCase().includes(query) ||
        request.status?.toLowerCase().includes(query) ||
        request.priority?.toLowerCase().includes(query) ||
        request.category_name?.toLowerCase().includes(query) ||
        request.request_note?.toLowerCase().includes(query)
      );
    });
  }, [requests, searchQuery, selectedCompanyId]);

  const handleCancel = (request: AssetRequest) => {
    setSelectedRequest(request);
    setShowCancelModal(true);
  };

  const handleCancelRequest = async (reason?: string) => {
    if (!selectedRequest) return;

    try {
      await dispatch(
        cancelRequestThunk({
          requestId: selectedRequest.id,
          cancellationReason: reason
        })
      ).unwrap();
      // The modal will close automatically after successful cancellation
    } catch (err) {
      console.error('Failed to cancel request:', err);
      // Error is handled inside the modal
      throw err; // Re-throw to let the modal handle the error
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      cancelled: { variant: 'secondary', label: 'Cancelled' },
      completed: { variant: 'info', label: 'Completed' },
      in_progress: { variant: 'primary', label: 'In Progress' },
      on_hold: { variant: 'secondary', label: 'On Hold' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      variant: 'secondary',
      label: status
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'success', label: 'Low' },
      medium: { variant: 'warning', label: 'Medium' },
      high: { variant: 'danger', label: 'High' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || {
      variant: 'secondary',
      label: priority
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns = useMemo<ColumnDef<AssetRequest>[]>(
    () => [
      {
        id: 'sn',
        header: 'SN',
        cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
        meta: {
          headerProps: {
            style: { width: '80px' }
          },
          cellProps: {}
        }
      },
      {
        accessorKey: 'asset_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faLaptop} className="me-2 text-primary" />
            Asset
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'category_name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
            Category
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-body">{(getValue() as string) || 'N/A'}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      // {
      //   accessorKey: 'priority',
      //   header: 'Priority',
      //   cell: ({ getValue }) => getPriorityBadge(getValue() as string),
      //   meta: {
      //     headerProps: {
      //       className: 'ps-2 bg-white py-3'
      //     }
      //   }
      // },
      // {
      //   accessorKey: 'status',
      //   header: 'Status',
      //   cell: ({ getValue }) => getStatusBadge(getValue() as string),
      //   meta: {
      //     headerProps: {
      //       className: 'ps-2 bg-white py-3'
      //     }
      //   }
      // },
      {
        accessorKey: 'priority',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="me-2 text-primary"
            />
            Priority
          </span>
        ),
        cell: ({ getValue }) => {
          const priority = (getValue() as string)?.toLowerCase();

          const config: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            high: {
              className: 'badge-phoenix-danger',
              icon: 'arrow-up',
              label: 'High'
            },
            medium: {
              className: 'badge-phoenix-warning',
              icon: 'minus',
              label: 'Medium'
            },
            low: {
              className: 'badge-phoenix-success',
              icon: 'arrow-down',
              label: 'Low'
            }
          };

          const badge = config[priority] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: priority
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'status',
        header: () => (
          <span>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="me-2 text-primary"
            />
            Status
          </span>
        ),
        cell: ({ getValue }) => {
          const status = (getValue() as string)?.toLowerCase();

          // Map status to Phoenix badge color and professional icon
          const statusConfig: Record<
            string,
            { className: string; icon: string; label: string }
          > = {
            fulfilled: {
              className: 'badge-phoenix-success', // green
              icon: 'check-circle', // professional “completed” icon
              label: 'Fulfilled'
            },
            'on-hold': {
              className: 'badge-phoenix-warning', // yellow
              icon: 'pause-circle', // professional “on-hold” icon
              label: 'On Hold'
            },
            approved: {
              className: 'badge-phoenix-primary', // blue
              icon: 'thumbs-up', // professional “approved” icon
              label: 'Approved'
            },
            rejected: {
              className: 'badge-phoenix-danger', // red
              icon: 'x-circle', // professional “rejected” icon
              label: 'Rejected'
            },
            pending: {
              className: 'badge-phoenix-info', // cyan/neutral
              icon: 'clock', // professional “pending” icon
              label: 'Pending'
            }
          };

          const badge = statusConfig[status] || {
            className: 'badge-phoenix-secondary',
            icon: 'help-circle',
            label: status
          };

          return (
            <span className={`badge badge-phoenix fs-10 ${badge.className}`}>
              <span className="badge-label">{badge.label}</span>
              <FeatherIcon icon={badge.icon} size={12} className="ms-1" />
            </span>
          );
        },
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'request_note',
        header: 'Notes',
        cell: ({ getValue }) => (
          <span className="text-body">
            {(getValue() as string) || 'No notes'}
          </span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        accessorKey: 'created_at',
        header: 'Requested On',
        cell: ({ getValue }) => (
          <span className="text-body">{formatDate(getValue() as string)}</span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      }
      // {
      //   id: 'actions',
      //   header: '',
      //   cell: ({ row: { original } }) => (
      //     <>
      //       {original.status === 'pending' ? (
      //         <Button
      //           variant="outline-warning"
      //           size="sm"
      //           onClick={() => handleCancel(original)}
      //         >
      //           Cancel
      //         </Button>
      //       ) : (
      //         <span className="text-muted">
      //           {original.status.charAt(0).toUpperCase() +
      //             original.status.slice(1)}
      //         </span>
      //       )}
      //     </>
      //   ),
      //   meta: {
      //     headerProps: {
      //       className: 'text-end pe-2 bg-white py-3'
      //     },
      //     cellProps: { className: 'text-end pe-2' }
      //   }
      // }
    ],
    []
  );

  const table = useAdvanceTable({
    data: filteredRequests,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  if (loading) {
    return <PhoenixLoader />;
  }

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {requests.length === 0 ? (
          <div className="text-center p-5">
            <h5>No Requests Found</h5>
            <p className="text-muted">
              You haven't made any asset requests yet.
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowY: 'visible' }}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table mb-0',
                  hover: true
                }}
                headerClassName=""
                rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
              />
            </div>
            <div className="table-footer-elegant">
              <AdvanceTableFooter pagination className="px-3 py-2" />
            </div>
          </>
        )}
      </div>

      {/* ✅ REPLACED: Use the new CancelRequestModal */}
      {selectedRequest && (
        <DeleteAllRequestModal
          show={showCancelModal}
          handleClose={() => {
            setShowCancelModal(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onCancel={handleCancelRequest}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default MyRequestsTable;
